import PropTypes from "prop-types";
import React from "react";

import { Accordion, Text } from "@swa-ui/core";
import { Currency } from "@swa-ui/currency";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import { FareComparisonSelector } from "../FareComparisonSelector";
import styles from "./CollapsibleFareOption.module.scss";

/**
 * A collapsible fare option component that displays fare information in an accordion format.
 * Shows a "Starting at" label with the fare amount in the header, and contains a FareComparisonSelector
 * in the collapsible content area.
 */
export const CollapsibleFareOption = (props) => {
  const { amount, className, currencyCode, onOptionSelect, selected } = props;

  return (
    <div
      className={classNames(className, {
        [styles.selected]: selected,
      })}
    >
      <Accordion {...getAccordionProps()}>
        {selected ? <FareComparisonSelector /> : <></>}
      </Accordion>
    </div>
  );

  function getAccordionProps() {
    return {
      fullWidthContent: true,
      label: renderAccordionHeader(),
      name: "collapsibleFareOptionAccordion",
      onChange: (isOpen) => isOpen && onOptionSelect?.(),
      revealed: selected,
      showPositionIndicator: true,
      styleType: "primary",
    };
  }

  function renderAccordionHeader() {
    return (
      <div className={styles.accordionHeaderLayout}>
        <Text color="var(--collapsible-fare-option-text)">
          {i18n("CollapsibleFareOption__STARTING_AT_LABEL")}
        </Text>
        <Currency {...getFareAmountProps()} />
      </div>
    );
  }

  function getFareAmountProps() {
    return {
      amount,
      currencyCode,
      roundingType: parseFloat(amount) > 0 ? "up" : "down",
      styleType: "primary",
      superScriptSymbol: true,
      textProps: {
        bold: true,
        color: "var(--collapsible-fare-option-text)",
        size: "xsmall",
        textType: "display",
      },
    };
  }
};

CollapsibleFareOption.propTypes = {
  /** The fare amount */
  amount: PropTypes.string.isRequired,

  /** Additional class name to apply to the component */
  className: PropTypes.string,

  /** Currency code for the fare amount (e.g., 'USD') */
  currencyCode: PropTypes.string.isRequired,

  /** Function called when the fare option is selected */
  onOptionSelect: PropTypes.func,

  /** Indicates if this fare option is currently selected */
  selected: PropTypes.bool,
};
